'use client'
import { forwardRef, useCallback } from 'react'
import classNames from 'classnames'

/**
 * Reusable TabButton component.
 * Replaces @achieve/ascend Button for lightweight tab-style use cases.
 *
 * @param {object} props
 * @param {'primary' | 'secondary'} [props.color='primary'] - Color theme
 * @param {'text' | 'contained' | 'outlined'} [props.variant='text'] - Visual variant
 * @param {'small' | 'medium' | 'large'} [props.size='medium'] - Size preset
 * @param {boolean} [props.disabled=false] - Disabled state
 * @param {boolean} [props.fullWidth=false] - Whether the button spans full width
 * @param {string} [props.className] - Additional Tailwind classes
 * @param {function} [props.onClick] - Click handler
 * @param {React.ReactNode} props.children - Button content
 */
const sizeClasses = {
  small: 'py-1 text-sm',
  medium: 'py-2 text-base',
  large: 'py-3 text-lg',
}

const variantClasses = {
  text: {
    primary:
      'bg-transparent text-[#3300FF] hover:bg-blue-50 focus-visible:ring-2 focus-visible:ring-blue-400',
    secondary:
      'bg-transparent text-gray-700 hover:bg-gray-100 focus-visible:ring-2 focus-visible:ring-gray-400',
  },
  contained: {
    primary:
      'bg-[#3300FF] text-white hover:bg-[#2600cc] focus-visible:ring-2 focus-visible:ring-blue-400',
    secondary:
      'bg-gray-200 text-gray-800 hover:bg-gray-300 focus-visible:ring-2 focus-visible:ring-gray-400',
  },
  outlined: {
    primary:
      'border border-[#3300FF] text-[#3300FF] bg-transparent hover:bg-blue-50 focus-visible:ring-2 focus-visible:ring-blue-400',
    secondary:
      'border border-gray-400 text-gray-700 bg-transparent hover:bg-gray-100 focus-visible:ring-2 focus-visible:ring-gray-400',
  },
}

const TabButton = forwardRef(function TabButton(
  {
    color = 'primary',
    variant = 'text',
    size = 'medium',
    disabled = false,
    fullWidth = false,
    className,
    onClick,
    children,
    ...rest
  },
  ref
) {
  const handleClick = useCallback(
    (e) => {
      if (!disabled && onClick) {
        onClick(e)
      }
    },
    [disabled, onClick]
  )

  return (
    <button
      ref={ref}
      type="button"
      disabled={disabled}
      onClick={handleClick}
      className={classNames(
        'inline-flex items-center justify-center rounded-md border-0 font-medium transition-colors duration-150 outline-hidden cursor-pointer',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        sizeClasses[size],
        variantClasses[variant]?.[color],
        fullWidth && 'w-full',
        className
      )}
      {...rest}
    >
      {children}
    </button>
  )
})

TabButton.displayName = 'TabButton'

export { TabButton }
export default TabButton
